import { useMutation, useQueryClient } from "react-query";
import { useDispatch } from "react-redux";

import { facilityMapRequestAtom } from "atoms/map/request/facilities";
import { wellMapRequestAtom } from "atoms/map/request/wells";
import { useAtomValue } from "jotai";
import { getCategoryList } from "store/features";

import { BulkUpdateRequest, bulkUpdateFieldGroup } from "api/data";

import { EntityKind } from "models/entityKind";
import { PolygonFilter } from "models/filter";

import { useFacilityMapUpdater } from "components/map/hooks/useFacilityMapUpdater";
import { useWellMapUpdater } from "components/map/hooks/useWellMapUpdater";

import { useFieldGroupDataQueryKeys } from "../queries";

export interface BulkUpdateFieldGroupParams {
  request: BulkUpdateRequest;
  entityKind: EntityKind;
  isOrg: boolean;
  projectId?: string | null;
}

export interface BulkUpdateFieldGroupMutationParams {
  fieldGroup: string;
  entityKind: EntityKind;
  isOrg: boolean;
  filterId: string;
  projectId?: string | null;
  polygonFilter?: PolygonFilter | null;
  onSuccess?: () => void | Promise<void>;
  onError?: (error) => void;
}

export function useBulkUpdateFieldGroupMutation(
  params: BulkUpdateFieldGroupMutationParams
) {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { fetchWellMap } = useWellMapUpdater();
  const { fetchFacilityMap } = useFacilityMapUpdater();

  const wellMapRequest = useAtomValue(wellMapRequestAtom);
  const facilityMapRequest = useAtomValue(facilityMapRequestAtom);

  return useMutation(
    async (params: BulkUpdateFieldGroupParams) => {
      return await bulkUpdateFieldGroup(
        params.request,
        params.entityKind,
        params.isOrg,
        params.projectId
      );
    },
    {
      onSuccess: async (_data, variables) => {
        // Invalidate the field group data query to trigger refetch
        queryClient.invalidateQueries([
          useFieldGroupDataQueryKeys.fieldGroupData,
          params.fieldGroup,
          params.entityKind,
          params.isOrg,
          params.filterId,
          params.projectId,
          params.polygonFilter
        ]);

        // Refresh the field selector category list to show new columns
        dispatch(getCategoryList(variables.entityKind));

        // Trigger map refresh to show updated field group data
        if (variables.entityKind === EntityKind.Well && wellMapRequest) {
          await fetchWellMap(wellMapRequest);
        } else if (variables.entityKind === EntityKind.Facility && facilityMapRequest) {
          await fetchFacilityMap(facilityMapRequest);
        }

        if (params.onSuccess) {
          await params.onSuccess();
        }
      },
      onError: (err) => {
        if (params.onError) {
          params.onError(err);
        }
      }
    }
  );
}
